import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { BokkzAlgDataService, BokkzIbDataService, IdentityProviderStatus, IncomeTaxEntity, KolommenbalansService, PmClient } from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { catchError, combineLatest, defer, distinctUntilChanged, EMPTY, filter, from, map, merge, of, shareReplay, take, tap, withLatestFrom } from 'rxjs';
import { ToastService } from '@snelstart/ui-toast';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { NavigationEnd, Router } from '@angular/router';
import { SharedUiLoadingModule } from '@snelstart/ui-loading';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';
import { OndernemingFormGroup, createOndernemingFormGroup } from './administratie-koppelen-ib.form';
import { switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-administratie-koppelen-ib',
    templateUrl: './administratie-koppelen-ib.component.html',
    styleUrls: ['./administratie-koppelen-ib.component.scss'],
    imports: [
        SharedUiLoadingModule,
        SharedUiButtonModule,
        SharedUiAlertModule,
        ReactiveFormsModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        CommonModule,
    ],
})
export class AdministratieKoppelenIbComponent implements OnInit, OnDestroy {
    dataService = inject(BokkzIbDataService);
    algDataService = inject(BokkzAlgDataService);
    router = inject(Router);
    toastService = inject(ToastService);
    pmClient = inject(PmClient);
    kolommenbalansService = inject(KolommenbalansService);
    formBuilder = inject(FormBuilder);
    destroyRef = inject(DestroyRef);

    formGroup: OndernemingFormGroup = createOndernemingFormGroup(this.formBuilder);

    client$ = this.algDataService.client.pipe(
        filter((client) => !!client?.id),
        tap((client) => console.debug(`Client: "${client.name}"`, client)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    administrations$ = this.client$.pipe(
        map((client) => client.adminEntities ?? []),
        tap((administrations) => {
            if (administrations.length === 0) {
                console.debug('No administrations loaded');
            }

            if (administrations.length > 0) {
                console.debug(`Loaded ${administrations.length} administrations`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    administrationOptions$ = this.administrations$.pipe(
        map((administrations) =>
            administrations.map<SelectFormControlItem<number>>((administration) => ({
                value: administration.id!,
                label: administration.name!,
            })),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    selectedAdministrationId$ = merge(
        defer(() => of(this.formGroup.controls.administrationId.value)),
        this.formGroup.controls.administrationId.valueChanges,
    ).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    selectedAdministration$ = combineLatest([this.administrations$, this.selectedAdministrationId$]).pipe(
        map(
            ([administrations, selectedAdministrationId]) =>
                administrations.find((administrationEntity) => administrationEntity.id === selectedAdministrationId) ?? null,
        ),
        distinctUntilChanged((previousAdministration, currentAdministration) => currentAdministration?.id === previousAdministration?.id),
        tap((administration) => {
            if (administration) {
                console.debug(`Administration: "${administration.name}"`, administration);
            } else {
                console.debug('No administration selected');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    identityProvider$ = this.selectedAdministration$.pipe(
        switchMap((administration) =>
            administration?.providerId ? this.pmClient.getIdentityProvider$(administration!.providerId) : of(null),
        ),
        distinctUntilChanged(
            (previousIdentityProvider, currentIdentityProvider) => currentIdentityProvider?.id === previousIdentityProvider?.id,
        ),
        tap((identityProvider) => {
            if (identityProvider) {
                console.debug(`Identity Provider: "${identityProvider.name}"`, identityProvider);
            } else {
                console.debug('No identity provider loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    identityProviderStatus$ = combineLatest([this.selectedAdministration$, this.identityProvider$]).pipe(
        switchMap(([administration, identityProvider]) => {
            if (administration && identityProvider && administration.providerId === identityProvider.id) {
                if (identityProvider.requiresEnvironment) {
                    if (administration.environmentId) {
                        return this.pmClient.getIdentityProviderEnvironmentStatus$(identityProvider.id, administration.environmentId);
                    }

                    return of({ id: identityProvider.id, name: identityProvider.name, authenticated: false } as IdentityProviderStatus);
                }

                return this.pmClient.getIdentityProviderStatus$(identityProvider.id);
            }

            return of(null);
        }),
        tap((identityProviderStatus) => {
            if (identityProviderStatus) {
                console.debug(`Loaded "${identityProviderStatus.name}" identity provider status`, identityProviderStatus);
            } else {
                console.debug('No identity provider status loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    incomeTax$ = this.dataService.incomeTaxEntity.pipe(
        filter((incomeTaxEntity) => !!incomeTaxEntity?.id),
        tap((incomeTaxEntity) => console.debug(`IncomeTaxEntity: "${incomeTaxEntity.name}"`, incomeTaxEntity)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ondernemingen$ = this.incomeTax$.pipe(
        map((incomeTax) => incomeTax.aangifte?.box1?.winstUitOnderneming?.ondernemingen ?? []),
        tap((ondernemingen) => {
            if (ondernemingen.length === 0) {
                console.debug('No ondernemingen loaded');
            }

            if (ondernemingen.length > 0) {
                console.debug(`Loaded ${ondernemingen.length} ondernemingen`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    onderneming$ = this.ondernemingen$.pipe(
        map((ondernemingen) => (ondernemingen.length > 0 ? ondernemingen[this.dataService.indexOnderneming.value] : null)),
        tap((onderneming) => {
            if (onderneming != null) {
                console.debug(`Onderneming: ${onderneming.naam}`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    importSnelStartBalanceButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.canImportSnelStartBalance$,
        onClick: () => this.importSnelStartBalance$,
        onError: (error) => console.error(error),
    });

    canImportSnelStartBalance$ = this.selectedAdministration$.pipe(
        take(1),
        withLatestFrom(this.ondernemingen$),
        map(([selectedAdministration, ondernemingen]) => {
            if (ondernemingen.length === 0) {
                this.toastService.showErrorMessage('Er zijn geen ondernemingen gevonden bij de aangifte.');
                return false;
            }

            if (!selectedAdministration) {
                this.toastService.showErrorMessage('Er is geen administratie geselecteerd.');
                return false;
            }

            return true;
        }),
    );

    importSnelStartBalance$ = combineLatest([this.client$, this.incomeTax$, this.onderneming$, this.selectedAdministration$]).pipe(
        take(1),
        switchMap(([client, incomeTax, onderneming, selectedAdministration]) => {
            console.info('Importing SnelStart administration', client, incomeTax, onderneming, selectedAdministration);

            if (!client || !incomeTax || !onderneming || !selectedAdministration) {
                return EMPTY;
            }

            onderneming.administratieId = selectedAdministration.administrationId!;

            return from(
                this.kolommenbalansService.getUpdatedAangifteMetAdministratie(
                    selectedAdministration.administrationId!,
                    client.sstRelationId!,
                    incomeTax,
                    false, // TODO: Aangever / Partner selectie (nu Aangever)
                    client.sstPrimaryAdminId!,
                ),
            ).pipe(map((incomeTax) => incomeTax as IncomeTaxEntity));
        }),
        tap((incomeTax) => {
            console.info('Aangifte bijgewerkt: ', incomeTax);

            this.dataService.updateIncomeTaxEntity(incomeTax);

            // TODO: Aangever / Partner en Onderneming selectie (nu Aangever en eerste Onderneming)
            this.toastService.showSuccessMessage(
                `De administratie ${incomeTax.aangifte?.aangifteJaar} van '${incomeTax.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].naam}' is succesvol ingelezen.`,
            );
        }),
        catchError((error) => {
            console.error(error);

            if (error.body) {
                this.toastService.showErrorMessage(error.body);
            }

            if (error.error) {
                this.toastService.showErrorMessage(error.error);
            } else {
                this.toastService.showErrorMessage(
                    'Helaas is er een technisch probleem waardoor de koppeling niet succesvol is verlopen. Wij werken hard aan een oplossing. In de tussentijd kun je verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
                );
            }

            return EMPTY;
        }),
    );

    importBalanceProviderBalanceButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.canImportBalanceProviderBalance$,
        onClick: () => this.importBalanceProviderBalance$,
        onError: (error) => console.error(error),
    });

    canImportBalanceProviderBalance$ = combineLatest([
        this.selectedAdministration$,
        this.ondernemingen$,
        this.identityProviderStatus$,
    ]).pipe(
        take(1),
        map(([selectedAdministration, ondernemingen, identityProviderStatus]) => {
            if (!selectedAdministration) {
                this.toastService.showErrorMessage('Er is geen administratie geselecteerd.');
                return false;
            }

            if (ondernemingen.length === 0) {
                this.toastService.showErrorMessage('Er zijn geen ondernemingen gevonden bij de aangifte.');
                return false;
            }

            const authenticated = !!identityProviderStatus?.authenticated;

            if (!authenticated) {
                this.toastService.showErrorMessage('U bent niet geautoriseerd om deze administratie in te lezen.');
            }

            return authenticated;
        }),
    );

    importBalanceProviderBalance$ = combineLatest([this.client$, this.incomeTax$, this.onderneming$, this.selectedAdministration$]).pipe(
        take(1),
        switchMap(([client, incomeTax, onderneming, selectedAdministration]) => {
            console.debug(
                `Importing ${selectedAdministration?.name} administration`,
                client,
                incomeTax,
                onderneming,
                selectedAdministration,
            );

            if (client == null || incomeTax == null || onderneming == null || selectedAdministration?.providerId == null) {
                return EMPTY;
            }

            onderneming.administratieId = selectedAdministration.administrationId!;

            if (selectedAdministration.environmentId) {
                return this.pmClient
                    .postBalanceProviderBalancesEnvironment(
                        client.sstRelationId!,
                        selectedAdministration.providerId!,
                        selectedAdministration.environmentId!,
                        selectedAdministration.administrationId!,
                        false,
                        incomeTax,
                    )
                    .pipe(map((incomeTax) => [incomeTax as IncomeTaxEntity, onderneming, selectedAdministration] as const));
            } else {
                return this.pmClient
                    .postBalanceProviderBalances(
                        client.sstRelationId!,
                        selectedAdministration.providerId!,
                        selectedAdministration.administrationId!,
                        false,
                        incomeTax,
                    )
                    .pipe(map((incomeTaxEntity) => [incomeTaxEntity as IncomeTaxEntity, onderneming] as const));
            }
        }),
        tap(([incomeTax, onderneming]) => {
            console.info('Aangifte bijgewerkt: ', incomeTax);

            this.dataService.updateIncomeTaxEntity(incomeTax);

            // TODO: Aangever / Partner en Onderneming selectie (nu Aangever en eerste Onderneming)
            this.toastService.showSuccessMessage(
                `De administratie ${incomeTax.aangifte?.aangifteJaar} van '${onderneming.naam}' is succesvol ingelezen.`,
            );
        }),
        catchError((error) => {
            console.error(error);
            this.toastService.showErrorMessage(
                'Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.',
            );
            return EMPTY;
        }),
    );

    removeOndernemingAdministrationButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.canRemoveOndernemingAdministration$,
        onClick: () => this.removeOndernemingAdministration$,
        onError: (error) => console.error(error),
    });

    canRemoveOndernemingAdministration$ = combineLatest([this.onderneming$, this.selectedAdministrationId$]).pipe(
        take(1),
        map(([onderneming, selectedAdministrationId]) => {
            return (onderneming != null && onderneming.administratieId != null) || selectedAdministrationId != null;
        }),
    );

    removeOndernemingAdministration$ = combineLatest([this.onderneming$, this.selectedAdministrationId$, this.incomeTax$]).pipe(
        take(1),
        tap(([onderneming, selectedAdministrationId, incomeTax]) => {
            if (selectedAdministrationId != null) {
                this.formGroup.controls.administrationId.setValue(null);
            }

            if (onderneming != null && onderneming.administratieId != null) {
                onderneming.administratieId = null;
                this.dataService.updateIncomeTaxEntity(incomeTax);
            }
        }),
    );

    ngOnInit(): void {
        this.incomeTax$
            .pipe(
                distinctUntilChanged((previousIncomeTax, currentIncomeTax) => previousIncomeTax.id === currentIncomeTax.id),
                withLatestFrom(this.onderneming$),
                map(([, onderneming]) => onderneming),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((onderneming) => {
                this.formGroup.patchValue({
                    naam: onderneming?.naam,
                    werkzaamheden: onderneming?.werkzaamheden,
                    ondernemingsvorm: onderneming?.ondernemingsvorm,
                });
            });

        combineLatest([this.onderneming$, this.administrations$])
            .pipe(
                map(
                    ([onderneming, administrations]) =>
                        administrations.find((administration) => administration.administrationId === onderneming?.administratieId) ?? null,
                ),
                filter((administration) => administration != null),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((administration) => {
                if (administration.id! !== this.formGroup.controls.administrationId.value) {
                    console.debug(`Selecting "${administration?.name}"`, administration);
                    this.formGroup.controls.administrationId.setValue(administration.id!);
                }
            });

        // Selecteer administratie indien dit de enige beschikbaar is.
        this.administrations$
            .pipe(
                map((administrations) => (administrations.length === 1 ? administrations[0] : null)),
                filter((administration) => administration != null),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((administration) => {
                if (administration.id !== this.formGroup.controls.administrationId.value) {
                    console.debug(`Selecting "${administration?.name}"`, administration);
                    this.formGroup.controls.administrationId.setValue(administration.id!);
                }
            });
    }

    ngOnDestroy(): void {
        if (!this.formGroup.dirty) {
            return;
        }

        this.incomeTax$.pipe(
            take(1),
            tap((incomeTax) => this.dataService.saveIncomeTaxEntity(false, incomeTax)),
        ).subscribe({
            error: (error: Error)=> console.error("Error saving income tax.", error),
        });
    }
}
